import {
  FEATURE_FLAGS,
  CommunicationCategory,
  ConfidentialityLevel,
  TimelineFilterType,
} from '@bariendo/lib/constants';
import { DATA_FROM_HEALTHIE_EXTENSION } from '@bariendo/lib/extensions';
import { normalizeTextContent } from '@bariendo/lib/services';
import { communicationHasAnyCategoryCode, isTimelinePatientFaxCommunication } from '@bariendo/lib/communication';
import { getExtension, getReferenceString } from '@medplum/core';
import {
  Communication,
  DiagnosticReport,
  OperationOutcome,
  Patient,
  Practitioner,
  Reference,
  Resource,
  Task,
} from '@medplum/fhirtypes';
import { MedicationRequestFragment } from '../graphql';
import dayjs from 'dayjs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useCompletedClinicalTasks } from '@/hooks/useCompletedClinicalTasks';
import { useFeatureFlagEnabled } from '@/hooks/useFeatureFlag';
import {
  PATIENT_DIAGNOSTIC_REPORTS_QUERY,
  PATIENT_ENCOUNTERS_QUERY,
  PATIENT_TIMELINE_QUERY,
  PATIENT_PRESCRIPTION_ORDERS_QUERY,
  PATIENT_APPOINTMENTS_QUERY,
  PATIENT_ELIGIBILITY_FORMS_QUERY,
  TimelineCommunicationResource,
  TimelineEncounterResource,
  AppointmentFragment,
  TimelineEligibilityFormResource,
} from '../graphql';
import { filterCommunications } from '@/services/Timeline/timelineFilter';
import { shouldShowHealthieDiagnosticReportInTimeline } from '@/services/Timeline/healthieDiagnosticReportFilter';
import {
  mapCommunications,
  mapDiagnosticReportsToTimelineItems,
  mapEncountersToTimelineItems,
  mapPrescriptionOrdersToTimelineItems,
  mapAppointmentsToTimelineItems,
  mapEligibilityFormsToTimelineItems,
} from '@/services/Timeline/timelineGroups';
import { ELIGIBILITY_QUESTIONNAIRE_URL } from '@/services/Timeline/eligibilityForm';
import { isDateInRange } from '@/services/date';
import { getQuestionnaireSearchableText } from '../services/questionnaireResponses';
import { FilterType } from '@/components/Timeline/TimelineFilters';
import {
  TimelineEncounter,
  TimelineLabOrderResults,
  TimelineNote,
  TimelinePatientCommunication,
  TimelinePrescriptionOrder,
  TimelineAppointment,
  TimelineEligibilityForm,
} from '@/components/Timeline/types';
import { useLiveGraphQL } from '@/hooks/useLiveGraphQL';
import { billingFilterOrAll } from '@/services/Timeline/billingFilter';
import { FILTER_CATEGORY_MAP } from './filterCategoryMap';

type HealthieLabResultPayload = { lab_order_id?: string };

function getHealthieLabOrderIdFromDiagnosticReport(report: DiagnosticReport): string | undefined {
  const raw = getExtension(report, DATA_FROM_HEALTHIE_EXTENSION)?.valueString;
  if (!raw) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw) as unknown as HealthieLabResultPayload;
    return typeof parsed?.lab_order_id === 'string' && parsed.lab_order_id.trim() ? parsed.lab_order_id : undefined;
  } catch {
    return undefined;
  }
}

function getDiagnosticReportTimelineDedupeKey(report: DiagnosticReport): string | undefined {
  const healthieLabOrderId = getHealthieLabOrderIdFromDiagnosticReport(report);
  if (healthieLabOrderId) {
    return `healthie-lab-order/${healthieLabOrderId}`;
  }

  const basedOnRef = report.basedOn?.[0] ? getReferenceString(report.basedOn[0]) : undefined;
  if (basedOnRef) {
    return `basedOn/${basedOnRef}`;
  }

  return report.id ? `id/${report.id}` : undefined;
}

function getDiagnosticReportRecencySortValue(report: DiagnosticReport): number {
  const lastUpdated = report.meta?.lastUpdated ? Date.parse(report.meta.lastUpdated) : Number.NaN;
  if (!Number.isNaN(lastUpdated)) {
    return lastUpdated;
  }
  if (report.issued) {
    const issued = Date.parse(report.issued);
    if (!Number.isNaN(issued)) {
      return issued;
    }
  }
  if (report.effectiveDateTime) {
    const effective = Date.parse(report.effectiveDateTime);
    if (!Number.isNaN(effective)) {
      return effective;
    }
  }
  return 0;
}

function dedupeDiagnosticReportsByMostRecentUpdate(items: TimelineLabOrderResults[]): TimelineLabOrderResults[] {
  const bestByKey = new Map<string, TimelineLabOrderResults>();
  const noKey: TimelineLabOrderResults[] = [];

  for (const item of items) {
    const key = getDiagnosticReportTimelineDedupeKey(item.diagnosticReport);
    if (!key) {
      noKey.push(item);
      continue;
    }

    const existing = bestByKey.get(key);
    if (!existing) {
      bestByKey.set(key, item);
      continue;
    }

    const existingRecency = getDiagnosticReportRecencySortValue(existing.diagnosticReport);
    const candidateRecency = getDiagnosticReportRecencySortValue(item.diagnosticReport);
    if (candidateRecency > existingRecency) {
      bestByKey.set(key, item);
    }
  }

  return [...bestByKey.values(), ...noKey].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

// Validate filter parameter from URL
const validateFilter = (filter: string | null): FilterType => {
  const validFilters: FilterType[] = [
    TimelineFilterType.ALL,
    TimelineFilterType.COMMUNICATIONS,
    TimelineFilterType.DOCUMENTATION,
    TimelineFilterType.CARE_CLINICAL,
    TimelineFilterType.APPOINTMENT,
    TimelineFilterType.BILLING,
    CommunicationCategory.SMS_MESSAGE,
    CommunicationCategory.PHONE_CALL,
    CommunicationCategory.CHAT,
    CommunicationCategory.STANDALONE_NOTE,
    CommunicationCategory.LAB_ORDER,
    CommunicationCategory.ENCOUNTER,
    CommunicationCategory.INBOUND,
    CommunicationCategory.FAX_MESSAGE,
  ];
  return validFilters.includes(filter as FilterType) ? (filter as FilterType) : TimelineFilterType.ALL;
};

// Parse date from URL parameter
const parseDate = (dateString: string | null): Date | null => {
  if (!dateString) return null;
  const date = dayjs(dateString);
  return date.isValid() ? date.toDate() : null;
};

const parseProviderReference = (providerString: string | null): Reference<Practitioner> | null => {
  if (!providerString || !providerString.startsWith('Practitioner/')) {
    return null;
  }
  return { reference: providerString };
};

export interface UseTimelineResult {
  activeFilter: FilterType;
  searchTerm: string;
  startDate: Date | null;
  endDate: Date | null;
  selectedProvider: Reference<Practitioner> | null;
  timelineItems: TimelinePatientCommunication[];
  filteredTimelineItems: TimelinePatientCommunication[];
  filteredTimelineNotes: TimelineNote[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  hasLoadedInitialData: boolean;
  encounters: TimelineEncounter[];
  diagnosticReports: TimelineLabOrderResults[];
  prescriptionOrders: TimelinePrescriptionOrder[];
  pastAppointments: TimelineAppointment[];
  futureAppointments: TimelineAppointment[];
  eligibilityForms: TimelineEligibilityForm[];
  /** Completed clinical tasks, rendered as "Task completed" rows. */
  completedClinicalTasks: Task[];
  prescriptionOrdersData: (MedicationRequestFragment & Resource)[];
  prescriptionOrdersLoading: boolean;
  prescriptionOrdersError: OperationOutcome | Error | undefined;
  error: OperationOutcome | Error | undefined;
  encountersError: OperationOutcome | Error | undefined;
  diagnosticReportsError: OperationOutcome | Error | undefined;
  appointmentsError: OperationOutcome | Error | undefined;
  eligibilityFormsError: OperationOutcome | Error | undefined;
  pauseLiveUpdates: () => void;
  resumeLiveUpdates: () => void;
  refresh: () => void;
  loadMore: () => Promise<void>;
}

interface UseTimelineProps {
  patient: Patient | undefined;
  urlSearchParams: URLSearchParams;
}

export function useTimeline(props: UseTimelineProps): UseTimelineResult {
  const { patient, urlSearchParams: searchParams } = props;

  const [areLiveUpdatesPaused, setAreLiveUpdatesPaused] = useState(false);

  // Finance cannot read Communication (BAR-2648). The communications query below is
  // the one whose error replaces the whole timeline, so a role without the grant must
  // not ask: empty variables skip it, as for the clinical queries.
  const {
    currentUserPermissions: { canViewClinicalRecord, canViewConversations, canViewPayments },
  } = useCurrentUser();

  const { activeFilter, searchTerm, startDate, endDate, selectedProvider } = useMemo(
    () => ({
      // A shared ?type=billing link opens on All for a role without the chip (BAR-2646).
      activeFilter: billingFilterOrAll(validateFilter(searchParams.get('type')), canViewPayments),
      searchTerm: searchParams.get('search') || '',
      startDate: parseDate(searchParams.get('startDate')),
      endDate: parseDate(searchParams.get('endDate')),
      selectedProvider: parseProviderReference(searchParams.get('provider')),
    }),
    [searchParams, canViewPayments]
  );
  const firstLoadTracker = useRef<[TimelinePatientCommunication[], TimelineNote[]]>(undefined);

  // Use patient reference as stable dependency to prevent refetching when patient object changes but reference stays the same
  const patientRef = patient ? getReferenceString(patient) : undefined;

  const queryVariables = useMemo(() => {
    if (!patientRef || !canViewConversations) return {};

    // When filter is 'encounter', 'appointment' or 'billing', skip fetching communications.
    // Billing has no Communication category: its cards come from the payments bot and the
    // benefit-event search, which the page gates on the same filter (BAR-2646).
    if (
      activeFilter === CommunicationCategory.ENCOUNTER ||
      activeFilter === TimelineFilterType.APPOINTMENT ||
      activeFilter === TimelineFilterType.BILLING
    ) {
      return {};
    }

    const categories = FILTER_CATEGORY_MAP[activeFilter] ?? [activeFilter, CommunicationCategory.TIMELINE_NOTE];

    firstLoadTracker.current = undefined;

    return {
      patientRef,
      categories: categories.join(','),
    };
  }, [patientRef, activeFilter, canViewConversations]);

  const {
    data,
    loading,
    hasLoadedInitialData,
    error,
    refresh: refreshTimeline,
    loadMore: loadMoreCommunications,
    loadingMore: loadingMoreCommunications,
    hasMore: hasMoreCommunications,
  } = useLiveGraphQL<TimelineCommunicationResource>(PATIENT_TIMELINE_QUERY, queryVariables, {
    pageSize: 100,
    pauseLiveUpdates: areLiveUpdatesPaused || !canViewConversations,
    liveUpdateDelay: 1,
    enablePagination: true,
  });

  // Add the Encounter query using GraphQL
  const encounterQueryVariables = useMemo(() => {
    if (!patientRef) return {};
    return {
      patientRef,
    };
  }, [patientRef]);

  // Encounters, results and prescriptions are the chart. A role that cannot read
  // it (Patient Access, BAR-2585) must not ask: empty variables skip the fetch,
  // and pausing live updates keeps useLiveGraphQL from opening a subscription on
  // the bare resource type when the patient variable is absent.
  const clinicalQueryVariables = useMemo(
    () => (canViewClinicalRecord ? encounterQueryVariables : {}),
    [canViewClinicalRecord, encounterQueryVariables]
  );
  const clinicalLiveUpdatesPaused = areLiveUpdatesPaused || !canViewClinicalRecord;

  const {
    data: encounters,
    error: encountersError,
    loading: encountersLoading,
    hasLoadedInitialData: encountersHasLoadedInitialData,
    refresh: refreshEncounters,
    loadMore: loadMoreEncounters,
    loadingMore: loadingMoreEncounters,
    hasMore: hasMoreEncounters,
  } = useLiveGraphQL<TimelineEncounterResource>(PATIENT_ENCOUNTERS_QUERY, clinicalQueryVariables, {
    pageSize: 100,
    pauseLiveUpdates: clinicalLiveUpdatesPaused,
    liveUpdateDelay: 1,
    enablePagination: true,
  });

  const {
    data: diagnosticReports,
    error: diagnosticReportsError,
    loading: diagnosticReportsLoading,
    refresh: refreshDiagnosticReports,
    loadMore: loadMoreDiagnosticReports,
    loadingMore: loadingMoreDiagnosticReports,
    hasMore: hasMoreDiagnosticReports,
    hasLoadedInitialData: diagnosticReportsHasLoadedInitialData,
  } = useLiveGraphQL<DiagnosticReport>(PATIENT_DIAGNOSTIC_REPORTS_QUERY, clinicalQueryVariables, {
    pageSize: 100,
    pauseLiveUpdates: clinicalLiveUpdatesPaused,
    liveUpdateDelay: 1,
    enablePagination: true,
  });

  // Add the Prescription Orders query using GraphQL
  const {
    data: prescriptionOrdersData,
    error: prescriptionOrdersError,
    loading: prescriptionOrdersLoading,
    hasLoadedInitialData: prescriptionOrdersHasLoadedInitialData,
    refresh: refreshPrescriptionOrders,
    loadMore: loadMorePrescriptionOrders,
    loadingMore: loadingMorePrescriptionOrders,
    hasMore: hasMorePrescriptionOrders,
  } = useLiveGraphQL<MedicationRequestFragment & Resource>(PATIENT_PRESCRIPTION_ORDERS_QUERY, clinicalQueryVariables, {
    pageSize: 100,
    pauseLiveUpdates: clinicalLiveUpdatesPaused,
    liveUpdateDelay: 1,
    enablePagination: true,
  });

  // Add the Appointments query using GraphQL
  const {
    data: appointmentsData,
    error: appointmentsError,
    loading: appointmentsLoading,
    hasLoadedInitialData: appointmentsHasLoadedInitialData,
    refresh: refreshAppointments,
    loadMore: loadMoreAppointments,
    loadingMore: loadingMoreAppointments,
    hasMore: hasMoreAppointments,
  } = useLiveGraphQL<AppointmentFragment & Resource>(PATIENT_APPOINTMENTS_QUERY, encounterQueryVariables, {
    pageSize: 100,
    pauseLiveUpdates: areLiveUpdatesPaused,
    liveUpdateDelay: 1,
    enablePagination: true,
    subscriptionParams: { patient: patientRef },
  });

  // Eligibility-form QRs (BAR-2495). The card sits in the Notes group, but being
  // a QuestionnaireResponse it is not reached by FILTER_CATEGORY_MAP, so the
  // query is skipped outright for filters that cannot show it — mirroring how the
  // communications query is skipped for the encounter and appointment filters.
  const eligibilityFormQueryVariables = useMemo(() => {
    if (!patientRef) return {};
    if (activeFilter !== TimelineFilterType.ALL && activeFilter !== TimelineFilterType.DOCUMENTATION) {
      return {};
    }
    return { patientRef, questionnaire: ELIGIBILITY_QUESTIONNAIRE_URL };
  }, [patientRef, activeFilter]);

  const {
    data: eligibilityFormsData,
    error: eligibilityFormsError,
    loading: eligibilityFormsLoading,
    hasLoadedInitialData: eligibilityFormsHasLoadedInitialData,
    refresh: refreshEligibilityForms,
    loadMore: loadMoreEligibilityForms,
    loadingMore: loadingMoreEligibilityForms,
    hasMore: hasMoreEligibilityForms,
  } = useLiveGraphQL<TimelineEligibilityFormResource & Resource>(
    PATIENT_ELIGIBILITY_FORMS_QUERY,
    eligibilityFormQueryVariables,
    {
      pageSize: 100,
      pauseLiveUpdates: areLiveUpdatesPaused,
      liveUpdateDelay: 1,
      enablePagination: true,
    }
  );

  const [allTimelineRegisters, allTimelineNotes] = useMemo(() => {
    if (data.length === 0) {
      return [[], []];
    }

    if (firstLoadTracker.current === undefined && data.length > 0) {
      // Do an initial load with just the items in the first page of results
      const firstItems = mapCommunications(data);
      firstLoadTracker.current = firstItems;
      return firstItems;
    }

    if (loading) {
      // Keep showing just the first items while we finish loading everything
      // so we don't trigger multiple re-renders for each new page of results
      return firstLoadTracker.current ?? [[], []];
    }

    return mapCommunications(data);
  }, [data, loading]);

  const [filteredTimelineRegisters, filteredTimelineNotes] = useMemo(() => {
    const [filteredRegisters, filteredNotes] = filterCommunications(
      allTimelineRegisters,
      allTimelineNotes,
      searchTerm,
      selectedProvider,
      startDate,
      endDate
    );

    if (activeFilter === CommunicationCategory.ENCOUNTER) {
      return [[], filteredNotes];
    }
    if (activeFilter === TimelineFilterType.APPOINTMENT || activeFilter === TimelineFilterType.BILLING) {
      return [[], []];
    }

    // Filter communications by category based on activeFilter
    const filterCategories = FILTER_CATEGORY_MAP[activeFilter];
    if (filterCategories) {
      // Exclude TIMELINE_NOTE from the filter since it's always included in the query
      const categoriesToFilter = filterCategories.filter((cat) => cat !== CommunicationCategory.TIMELINE_NOTE);
      const categorySet = new Set(categoriesToFilter);

      const filteredComms = filteredRegisters.filter(
        (item) =>
          communicationHasAnyCategoryCode(item.resource, categorySet) ||
          (categorySet.has(CommunicationCategory.FAX_MESSAGE) &&
            isTimelinePatientFaxCommunication(item.resource as Communication))
      );
      return [filteredComms, filteredNotes];
    }

    return [filteredRegisters, filteredNotes];
  }, [allTimelineRegisters, allTimelineNotes, searchTerm, selectedProvider, startDate, endDate, activeFilter]);

  const allEncounters = useMemo(() => {
    return mapEncountersToTimelineItems(encounters ?? []);
  }, [encounters]);

  const allDiagnosticReports = useMemo(() => {
    return mapDiagnosticReportsToTimelineItems(diagnosticReports ?? []);
  }, [diagnosticReports]);

  const { currentUserPermissions, currentUser } = useCurrentUser();

  const filteredDiagnosticReports = useMemo(() => {
    let filtered = allDiagnosticReports;

    // Filter by activeFilter - show diagnostic reports only when filter is 'all' or 'care-clinical'
    if (activeFilter !== TimelineFilterType.ALL && activeFilter !== TimelineFilterType.CARE_CLINICAL) {
      return [];
    }

    // Check if user has break-glass access
    const hasBreakGlassAccess =
      patient?.meta?.accounts?.some((account) => getReferenceString(account) === getReferenceString(currentUser!)) ??
      false;

    // Filter out Restricted DiagnosticReports if user doesn't have Restricted access
    // Admins can see Restricted content (though payload may be hidden), and users with Restricted roles can see it
    // All DiagnosticReports should be Restricted, so filter out any that are Restricted or undefined for non-Restricted users
    const hasRestrictedAccess =
      currentUserPermissions.canAccessRestrictedContent ||
      currentUserPermissions.isAdmin ||
      currentUserPermissions.hasAdminMembership ||
      hasBreakGlassAccess;
    if (!hasRestrictedAccess) {
      filtered = filtered.filter((report) => {
        return report.confidentiality === ConfidentialityLevel.NORMAL;
      });
    }

    // Filter by date range if provided
    if (startDate || endDate) {
      filtered = filtered.filter((report) => isDateInRange(report.timestamp, startDate, endDate));
    }

    // Filter by provider if selected
    if (selectedProvider) {
      const selectedProviderRef = getReferenceString(selectedProvider);
      filtered = filtered.filter(({ diagnosticReport }) => {
        // Check if the performer matches the selected provider
        return diagnosticReport.performer?.some((performer: Reference | undefined) => {
          if (!performer) return false;
          const performerRef = getReferenceString(performer);
          return performerRef === selectedProviderRef;
        });
      });
    }

    filtered = filtered.filter(({ diagnosticReport }) =>
      shouldShowHealthieDiagnosticReportInTimeline(diagnosticReport)
    );

    // Healthie can emit multiple DiagnosticReports for the same lab order. Collapse them into one timeline item,
    // keeping the most recently updated report (meta.lastUpdated), then sort by issued timestamp (already on item.timestamp).
    filtered = dedupeDiagnosticReportsByMostRecentUpdate(filtered);

    // Note: Diagnostic reports are not searchable by text content
    // They can be filtered by date range and provider only

    return filtered;
  }, [
    allDiagnosticReports,
    activeFilter,
    startDate,
    endDate,
    selectedProvider,
    currentUserPermissions.canAccessRestrictedContent,
    currentUserPermissions.isAdmin,
    currentUserPermissions.hasAdminMembership,
    currentUser,
    patient,
  ]);

  const allPrescriptionOrders = useMemo(() => {
    return mapPrescriptionOrdersToTimelineItems(prescriptionOrdersData ?? []);
  }, [prescriptionOrdersData]);

  const { pastAppointments, futureAppointments } = useMemo(() => {
    const allAppointments = mapAppointmentsToTimelineItems(appointmentsData ?? []);

    // Deduplicate by appointment id - subscription + refresh race can sometimes produce duplicates
    const seenIds = new Set<string>();
    const dedupedAppointments = allAppointments.filter((apt) => {
      const id = apt.appointment.id;
      if (!id || seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });

    return dedupedAppointments.reduce(
      (acc, appointment) => {
        // Cancelled (and rescheduled) appointments stay in the chart as a record of
        // the booking; the card marks them so they don't read as active.
        if (dayjs(appointment.appointment.start).isBefore(dayjs())) {
          acc.pastAppointments.push(appointment);
        } else {
          acc.futureAppointments.push(appointment);
        }
        return acc;
      },
      { pastAppointments: [] as TimelineAppointment[], futureAppointments: [] as TimelineAppointment[] }
    );
  }, [appointmentsData]);

  // Helper function to create searchable text from prescription order
  const getPrescriptionOrderSearchableText = useCallback(
    ({ medicationRequest, medicationDisplay, dosageInstruction }: TimelinePrescriptionOrder): string => {
      const parts: (string | undefined)[] = [
        medicationDisplay,
        dosageInstruction,
        medicationRequest.status,
        medicationRequest.requester?.display,
      ];

      return parts.filter(Boolean).join(' ');
    },
    []
  );

  // Helper function to create searchable text from encounter
  const getEncounterSearchableText = useCallback(({ encounter: encounterResource }: TimelineEncounter): string => {
    const parts: string[] = [];

    // Add encounter ID
    if (encounterResource.id) {
      parts.push(encounterResource.id);
    }

    // Add encounter type
    if (encounterResource.type) {
      encounterResource.type.forEach((type) => {
        type.coding?.forEach((coding) => {
          if (coding.display) parts.push(coding.display);
          if (coding.code) parts.push(coding.code);
        });
        if (type.text) parts.push(type.text);
      });
    }

    // Add encounter status
    if (encounterResource.status) {
      parts.push(encounterResource.status);
    }

    // Add encounter class
    if (encounterResource.class) {
      if (encounterResource.class.display) {
        parts.push(encounterResource.class.display);
      }
      if (encounterResource.class.code) {
        parts.push(encounterResource.class.code);
      }
    }

    // Add reason code
    if (encounterResource.reasonCode) {
      encounterResource.reasonCode.forEach((reason) => {
        reason.coding?.forEach((coding) => {
          if (coding.display) parts.push(coding.display);
          if (coding.code) parts.push(coding.code);
        });
        if (reason.text) parts.push(reason.text);
      });
    }

    if (encounterResource.tasks && encounterResource.tasks.length > 0) {
      const questionnaireText = getQuestionnaireSearchableText(encounterResource.tasks);
      if (questionnaireText) {
        parts.push(questionnaireText);
      }
    }

    return parts.join(' ');
  }, []);

  const filteredEncounters = useMemo(() => {
    let filtered = allEncounters;

    // Filter by activeFilter - show encounters only when filter is 'encounter', 'all', or 'documentation'
    if (
      activeFilter !== TimelineFilterType.ALL &&
      activeFilter !== CommunicationCategory.ENCOUNTER &&
      activeFilter !== TimelineFilterType.DOCUMENTATION
    ) {
      return [];
    }

    // Filter by date range if provided
    if (startDate || endDate) {
      filtered = filtered.filter((encounter) => isDateInRange(encounter.timestamp, startDate, endDate));
    }

    // Filter by provider if selected
    if (selectedProvider) {
      const selectedProviderRef = getReferenceString(selectedProvider);
      filtered = filtered.filter(({ encounter: encounterResource }) => {
        // Check if any participant matches the selected provider
        return encounterResource.participant?.some((participant) => {
          if (!participant.individual) return false;
          const participantRef = getReferenceString(participant.individual);
          return participantRef === selectedProviderRef;
        });
      });
    }

    // Filter by search term if provided
    if (searchTerm.trim()) {
      const searchLower = normalizeTextContent(searchTerm);
      filtered = filtered.filter((encounter) => {
        const searchableText = getEncounterSearchableText(encounter);
        return normalizeTextContent(searchableText).includes(searchLower);
      });
    }

    return filtered;
  }, [allEncounters, activeFilter, startDate, endDate, selectedProvider, searchTerm, getEncounterSearchableText]);

  const filteredPrescriptionOrders = useMemo(() => {
    let filtered = allPrescriptionOrders;

    // Filter by activeFilter - show prescription orders only when filter is 'all' or 'care-clinical'
    if (activeFilter !== TimelineFilterType.ALL && activeFilter !== TimelineFilterType.CARE_CLINICAL) {
      return [];
    }

    // Filter by date range if provided
    if (startDate || endDate) {
      filtered = filtered.filter((prescriptionOrder) => isDateInRange(prescriptionOrder.timestamp, startDate, endDate));
    }

    // Filter by provider if selected
    if (selectedProvider) {
      const selectedProviderRef = getReferenceString(selectedProvider);
      filtered = filtered.filter(({ medicationRequest }) => {
        // Check if the requester matches the selected provider
        if (!medicationRequest.requester) return false;
        const requesterRef = getReferenceString(medicationRequest.requester);
        return requesterRef === selectedProviderRef;
      });
    }

    // Filter by search term if provided
    if (searchTerm.trim()) {
      const searchLower = normalizeTextContent(searchTerm);
      filtered = filtered.filter((prescriptionOrder) => {
        const searchableText = getPrescriptionOrderSearchableText(prescriptionOrder);
        return normalizeTextContent(searchableText).includes(searchLower);
      });
    }

    return filtered;
  }, [
    allPrescriptionOrders,
    activeFilter,
    startDate,
    endDate,
    selectedProvider,
    searchTerm,
    getPrescriptionOrderSearchableText,
  ]);

  const filteredEligibilityForms = useMemo(() => {
    // Same visibility rule as the query above; kept here too so a cached page of
    // results can't leak into a filter that shouldn't show it.
    if (activeFilter !== TimelineFilterType.ALL && activeFilter !== TimelineFilterType.DOCUMENTATION) {
      return [];
    }

    let filtered = mapEligibilityFormsToTimelineItems(eligibilityFormsData ?? []);

    if (startDate || endDate) {
      filtered = filtered.filter((form) => isDateInRange(form.timestamp, startDate, endDate));
    }

    // No provider filter: the patient fills this in on everself.com with nobody
    // on our team involved, so there is no provider to match against.
    if (selectedProvider) {
      return [];
    }

    return filtered;
  }, [eligibilityFormsData, activeFilter, startDate, endDate, selectedProvider]);

  const pauseLiveUpdates = useCallback(() => {
    setAreLiveUpdatesPaused(true);
  }, []);

  const resumeLiveUpdates = useCallback(() => {
    setAreLiveUpdatesPaused(false);
  }, []);

  // Completed clinical tasks are a timeline stream like any other: owned here
  // so the composite refresh() below drives it, which is what makes a
  // completion on the Tasks tab surface immediately (see TasksManagement).
  const tasksTimelineEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_TASK_MANAGEMENT);
  const { tasks: completedClinicalTasks, refresh: refreshCompletedClinicalTasks } = useCompletedClinicalTasks(
    tasksTimelineEnabled ? patient : undefined
  );

  const refresh = useCallback(() => {
    setAreLiveUpdatesPaused(false);
    refreshTimeline();
    refreshEncounters();
    refreshDiagnosticReports();
    refreshPrescriptionOrders();
    refreshAppointments();
    refreshEligibilityForms();
    void refreshCompletedClinicalTasks();
  }, [
    refreshTimeline,
    refreshEncounters,
    refreshDiagnosticReports,
    refreshPrescriptionOrders,
    refreshAppointments,
    refreshEligibilityForms,
    refreshCompletedClinicalTasks,
  ]);

  // When filter skips the communications query, only consider the relevant loading states
  const combinedLoading = useMemo(() => {
    if (activeFilter === CommunicationCategory.ENCOUNTER) {
      return encountersLoading;
    }
    if (activeFilter === TimelineFilterType.APPOINTMENT) {
      return appointmentsLoading;
    }
    // Billing draws none of these streams; the page waits on the billing reads instead.
    if (activeFilter === TimelineFilterType.BILLING) {
      return false;
    }
    return (
      loading ||
      encountersLoading ||
      diagnosticReportsLoading ||
      prescriptionOrdersLoading ||
      appointmentsLoading ||
      eligibilityFormsLoading
    );
  }, [
    activeFilter,
    loading,
    encountersLoading,
    diagnosticReportsLoading,
    prescriptionOrdersLoading,
    appointmentsLoading,
    eligibilityFormsLoading,
  ]);

  const combinedHasLoadedInitialData = useMemo(() => {
    if (activeFilter === CommunicationCategory.ENCOUNTER) {
      return encountersHasLoadedInitialData;
    }

    if (activeFilter === TimelineFilterType.APPOINTMENT) {
      return appointmentsHasLoadedInitialData;
    }

    if (activeFilter === TimelineFilterType.BILLING) {
      return true;
    }

    return (
      hasLoadedInitialData &&
      prescriptionOrdersHasLoadedInitialData &&
      diagnosticReportsHasLoadedInitialData &&
      appointmentsHasLoadedInitialData &&
      eligibilityFormsHasLoadedInitialData
    );
  }, [
    activeFilter,
    encountersHasLoadedInitialData,
    hasLoadedInitialData,
    prescriptionOrdersHasLoadedInitialData,
    diagnosticReportsHasLoadedInitialData,
    appointmentsHasLoadedInitialData,
    eligibilityFormsHasLoadedInitialData,
  ]);

  const loadMore = useCallback(async () => {
    // If we didn't load the initial data yet, don't load more
    // This is to avoid an error that can ocurrer when useLoadMoreUntilReferenceIsFound calls this function
    if (firstLoadTracker.current === undefined) {
      return;
    }

    const promises = [
      loadMoreCommunications(),
      loadMoreDiagnosticReports(),
      loadMorePrescriptionOrders(),
      loadMoreEncounters(),
      loadMoreAppointments(),
      loadMoreEligibilityForms(),
    ];

    await Promise.all(promises);
  }, [
    loadMoreCommunications,
    loadMoreEncounters,
    loadMoreDiagnosticReports,
    loadMorePrescriptionOrders,
    loadMoreAppointments,
    loadMoreEligibilityForms,
  ]);

  const combinedLoadingMore =
    loadingMoreEncounters ||
    loadingMoreAppointments ||
    loadingMoreCommunications ||
    loadingMoreDiagnosticReports ||
    loadingMorePrescriptionOrders ||
    loadingMoreEligibilityForms;

  // Nothing under Billing pages: its cards are a handful per patient, read whole.
  const combinedHasMore =
    activeFilter !== TimelineFilterType.BILLING &&
    (hasMoreEncounters ||
      hasMoreAppointments ||
      hasMoreCommunications ||
      hasMoreDiagnosticReports ||
      hasMorePrescriptionOrders ||
      hasMoreEligibilityForms);

  return {
    activeFilter,
    searchTerm,
    startDate,
    endDate,
    selectedProvider,
    timelineItems: allTimelineRegisters,
    filteredTimelineItems: filteredTimelineRegisters,
    filteredTimelineNotes: filteredTimelineNotes,
    loading: combinedLoading,
    loadingMore: combinedLoadingMore,
    hasMore: combinedHasMore,
    hasLoadedInitialData: combinedHasLoadedInitialData,
    error,
    encounters: filteredEncounters,
    diagnosticReports: filteredDiagnosticReports,
    prescriptionOrders: filteredPrescriptionOrders,
    pastAppointments,
    futureAppointments,
    prescriptionOrdersData: prescriptionOrdersData ?? [],
    prescriptionOrdersLoading,
    prescriptionOrdersError,
    encountersError,
    diagnosticReportsError,
    appointmentsError,
    eligibilityForms: filteredEligibilityForms,
    eligibilityFormsError,
    completedClinicalTasks,
    pauseLiveUpdates,
    resumeLiveUpdates,
    refresh,
    loadMore,
  };
}
